import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  atlasAskStream,
  atlasBrief,
  atlasMemoryForget,
  atlasMemoryRestore,
  atlasProposalApprove,
  consoleAnswerLinks,
  emptyAnswerTurn,
  scribeSessions,
  type AtlasAnswerTurn,
  type AtlasBrief,
  type AtlasContext,
  type AtlasDryRun,
  type AtlasStep,
  type AtlasTone,
  type ScribeSessionsResponse,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { userKey } from '../../session/store';
import { useQuery, type QueryState } from '../../useQuery';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { sx } from '../../ui/designStyles';
import { AMBER, BLUE, DIM, EASE, INK, MUTE, OK, RED, inWords, mark, noDash, stop, useGo, useNow, type AtlasRole } from './common';
import { EvidenceBlock } from './Evidence';
import { sessionRow } from './scribeCommon';
import { useGreeting } from '../../ui/greeting';

type Turn =
  | { kind: 'you'; text: string }
  | { kind: 'atlas'; q: string; answer: AtlasAnswerTurn; error: string | null }
  | { kind: 'dry'; dry: AtlasDryRun; ran: string | null; busy: boolean }
  | { kind: 'ran'; text: string; sealed: string | null };

const COL = 'display:flex;flex-direction:column;min-height:0;';
const FOLLOW = 'display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:7px;background:rgba(47,92,151,.11);color:#1D3F6B;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;transition:background .18s ' + EASE + ';';
const TEXT = 'font-size:16px;color:' + INK + ';line-height:1.55;margin-top:5px;max-width:70ch;text-wrap:pretty;';
const TEXT_YOU = 'font-size:16px;font-weight:700;color:' + INK + ';line-height:1.5;margin-top:5px;max-width:70ch;text-wrap:pretty;';
const TRACE_ROW = 'display:flex;align-items:baseline;gap:9px;padding:2px 0;min-width:0;';
const HEAD = 'font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;';

/**
 * A thread kept for this tab and this person only (sessionStorage, under their own key):
 * answers can name residents. Removed at sign-out (store.ts clearPerUserData).
 */
function loadThread(key: string | null): Turn[] {
  if (!key) return [];
  try {
    const raw = window.sessionStorage.getItem(key);
    const v = raw ? (JSON.parse(raw) as Turn[]) : [];
    return Array.isArray(v) ? v.filter((t) => t.kind !== 'atlas' || t.answer.done || t.error) : [];
  } catch {
    return [];
  }
}

const msText = (ms: number | null) => (ms == null ? '–' : ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(2).replace(/(\.\d)0$/, '$1')}s`);

/** The working, from the answer: served per-step timings when sent, otherwise what the stream carried. */
/** Only the Aithora team sees which AI vendor and model answered (the server blanks them for
 *  everyone else; this keeps a client role from ever drawing one if a turn still carries it). */
const VENDOR_ROLES: ReadonlySet<string> = new Set(['SUPER_ADMIN', 'OPERATOR', 'DEVELOPER']);
const COMPOSED = 'composed by Atlas';

function stepsOf(a: AtlasAnswerTurn, showVendor = false): AtlasStep[] {
  if (a.steps) return showVendor ? a.steps : a.steps.map((s) => (s.step === 'REASON' && !/^no live model|^not composed/.test(s.detail) ? { ...s, detail: COMPOSED + (/time includes retrieval/.test(s.detail) ? ' (time includes retrieval)' : '') } : s));
  const out: AtlasStep[] = [];
  if (a.route) out.push({ step: 'ROUTE', detail: `${a.route.intent} · ${a.route.owner}`, ms: null });
  if (a.route || a.tools.length || a.retrieval) {
    const names = a.tools.length ? a.tools.map((t) => t.name) : a.retrieval?.tools ?? [];
    out.push({ step: 'RETRIEVE', detail: names.length ? names.join(', ') : 'nothing retrieved', ms: null });
  }
  if (a.route) out.push({ step: 'REASON', detail: !a.route.live ? 'not composed' : showVendor && a.route.provider ? `${a.route.provider} · ${a.route.model}` : COMPOSED, ms: null });
  if (a.gate) out.push({ step: 'GOVERN', detail: `${a.gate.result} · ${noDash(a.gate.reason)}`, ms: a.gate.latencyMs });
  return out;
}

/** The ask box's hint, per role, worded as the design's atkPlaceholder. Sales has no Atlas
 *  lens of its own (it reads as Operator), so it is told apart by the account's role. */
export function placeholderFor(ar: AtlasRole, role: string | null, person: string | null = null): string {
  if (role === 'SALES') return 'Ask about prospects or the catalog';
  // Family (V7 atkPlaceholder): their own person, by name.
  if (ar === 'Family') return person ? `Ask about ${person}` : 'Ask about your person';
  if (ar === 'Care Staff') return 'Ask about your residents or your shift';
  if (ar === 'Developer') return 'Ask about services, incidents or the ledger';
  if (ar === 'Partner') return 'Ask about any building';
  return 'Ask about a resident, an incident, a facility';
}

const refusedOf = (a: AtlasAnswerTurn) => a.gate?.result === 'BLOCK';

/** The marks under a settled answer: served when sent, otherwise read from the route and the gate. */
function marksOf(a: AtlasAnswerTurn, error: string | null): { label: string; tone: AtlasTone }[] {
  if (error) return [{ label: 'COULD NOT REACH', tone: 'red' }, { label: 'NOTHING COMPOSED', tone: 'mute' }];
  if (a.marks) return a.marks;
  if (refusedOf(a)) return [{ label: 'REFUSED', tone: 'red' }, ...(a.tools.length ? [] : [{ label: 'NOTHING RETRIEVED', tone: 'mute' as AtlasTone }])];
  const out: { label: string; tone: AtlasTone }[] = [];
  if (a.route) out.push(a.route.live ? { label: 'LIVE', tone: 'ok' } : { label: 'NO LIVE MODEL', tone: 'amber' });
  if (a.withheld) out.push({ label: 'WITHHELD', tone: 'red' });
  else if (a.gate?.result === 'PASS') out.push({ label: 'GOVERNED', tone: 'blue' });
  else if (a.gate?.result === 'HOLD') out.push({ label: 'FLAGGED', tone: 'vio' });
  if (a.sealed) out.push({ label: 'SEALED ' + a.sealed, tone: 'mute' });
  return out;
}

/**
 * The Chat tab (design: atIsChat, T13061; atlasChat L31196): what Atlas may read for this
 * role on the left, the conversation on the right. Answers stream from POST /api/atlas/ask;
 * nothing Atlas proposes runs until a person signs it.
 */
export function ChatTab({ ar, ctx, railOpen, W, onScribe }: { ar: AtlasRole; ctx: QueryState<AtlasContext>; railOpen: boolean; W: number; onScribe: () => void }) {
  const { client, session } = useSession();
  const hello = useGreeting();
  const store = userKey('aithora.atlas.thread', session?.user.id);
  const go = useGo();
  const now = useNow();
  const roReason = useReadOnlyReason();
  const stacked = W < 820;
  const c = ctx.data;
  const [scopePick, setScopePick] = useState<string | null>(null);
  const scopes = c?.scopes ?? [];
  const scopeId = c?.scope.fixed ? null : scopePick ?? scopes[0]?.id ?? null;
  const scopeName = c?.scope.fixed ? c.scope.name : scopes.find((s) => s.id === scopeId)?.name ?? scopes[0]?.name ?? null;

  const brief = useQuery<AtlasBrief>((signal) => atlasBrief(client, { query: scopeId ? { scope: scopeId } : {}, signal }), [client, scopeId]);
  // Scribe sessions are resident PHI: the server refuses Partner, Developer and Sales (Atlas for
  // a Developer is Chat and Eval; for Sales, Chat only), so none of them asks for them.
  const { role: who } = useConsoleRole();
  const scribe = useQuery<ScribeSessionsResponse>((signal) => scribeSessions(client, { signal }), [client], { enabled: ar !== 'Partner' && ar !== 'Developer' && ar !== 'Family' && who !== 'SALES' });
  const [thread, setThread] = useState<Turn[]>(() => loadThread(store));
  const [input, setInput] = useState('');
  const [openTrace, setOpenTrace] = useState<number | null>(null);
  const [forgot, setForgot] = useState<Record<string, boolean>>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const askCtl = useRef<AbortController | null>(null);

  useEffect(() => {
    try {
      if (store) window.sessionStorage.setItem(store, JSON.stringify(thread));
    } catch {
      /* a convenience only */
    }
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread, store]);
  useEffect(() => () => askCtl.current?.abort(), []);

  const suggestions = brief.data?.suggestions ?? [];
  const setLast = (patch: (t: Turn) => Turn) =>
    setThread((th) => {
      const i = th.length - 1;
      return i < 0 ? th : th.map((t, k) => (k === i ? patch(t) : t));
    });

  const ask = (q: string) => {
    const question = q.trim();
    if (!question) return;
    askCtl.current?.abort();
    const ctl = new AbortController();
    askCtl.current = ctl;
    const history: { role: 'user' | 'assistant'; content: string }[] = thread.flatMap((t): { role: 'user' | 'assistant'; content: string }[] =>
      t.kind === 'you' ? [{ role: 'user', content: t.text }] : t.kind === 'atlas' && t.answer.text ? [{ role: 'assistant', content: t.answer.text }] : [],
    );
    setThread((th) => [...th, { kind: 'you', text: question }, { kind: 'atlas', q: question, answer: emptyAnswerTurn(), error: null }]);
    setInput('');
    setOpenTrace(null);
    atlasAskStream(client, { question, history, scope: scopeId }, { signal: ctl.signal, onTurn: (a) => setLast((t) => (t.kind === 'atlas' ? { ...t, answer: a } : t)) })
      .then((a) => setLast((t) => (t.kind === 'atlas' ? { ...t, answer: a } : t)))
      .catch((e: unknown) => {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        setLast((t) => (t.kind === 'atlas' ? { ...t, error: e instanceof Error ? e.message : 'Atlas could not answer.' } : t));
      });
  };

  const openDry = (d: AtlasDryRun) => setThread((th) => [...th, { kind: 'dry', dry: d, ran: null, busy: false }]);
  const cancelDry = (i: number) => setThread((th) => th.filter((_, k) => k !== i));
  const doIt = async (i: number, d: AtlasDryRun) => {
    setThread((th) => th.map((t, k) => (k === i && t.kind === 'dry' ? { ...t, busy: true } : t)));
    try {
      const r = await atlasProposalApprove(client, { params: { id: d.id }, body: {} });
      const executed = r.status === 'executed';
      const left = Math.max(0, (r.proposal?.requiredApprovals ?? 0) - (r.proposal?.approvalsCount ?? 0));
      const note = executed
        ? noDash(d.ran ?? 'Ran. Sealed to the Truth Ledger under your name.')
        : left > 0
          ? `Signed. It needs ${inWords(left)} more signature${left === 1 ? '' : 's'} before it runs, on the Actions tab.`
          : noDash(r.reason ?? r.error ?? 'The gate did not run it. It stays on the Actions tab.');
      setThread((th) => [
        ...th.map((t, k) => (k === i && t.kind === 'dry' ? { ...t, busy: false, ran: executed ? note : null } : t)),
        { kind: 'ran', text: note, sealed: executed ? r.proposal?.ledgerHash?.slice(0, 8) ?? null : null },
      ]);
    } catch (e) {
      setThread((th) => [...th.map((t, k) => (k === i && t.kind === 'dry' ? { ...t, busy: false } : t)), { kind: 'ran', text: e instanceof Error ? e.message : 'It did not run.', sealed: null }]);
    }
  };
  const ranIds = new Set(thread.flatMap((t) => (t.kind === 'dry' && t.ran ? [t.dry.id] : [])));

  const forget = async (id: string, on: boolean) => {
    setForgot((f) => ({ ...f, [id]: on }));
    try {
      await (on ? atlasMemoryForget : atlasMemoryRestore)(client, { params: { id } });
    } catch {
      setForgot((f) => ({ ...f, [id]: !on }));
    }
  };

  const reachN = (c?.systems ?? []).filter((y) => y.inReach).length;
  const sess = (scribe.data?.sessions ?? []).map((s) => sessionRow(s, now));
  const scribeShow = thread.length === 0 && sess.length > 0;
  const placeholder = placeholderFor(ar, who, ar === 'Family' ? scopeName : null);

  return (
    <div
      data-testid="atlas-chat"
      style={sx(
        stacked
          ? 'flex:1 1 auto;display:flex;flex-direction:column;gap:16px;min-width:0;overflow:hidden;height:calc(100vh - 196px);min-height:320px;'
          : 'flex:1 1 auto;display:flex;align-items:stretch;gap:24px;min-width:0;flex-wrap:nowrap;overflow:hidden;position:relative;height:calc(100vh - 196px);min-height:320px;',
      )}
    >
      {railOpen && (
        <div
          data-testid="atlas-rail"
          style={sx(
            W >= 1120 && !stacked
              ? COL + 'flex:0 0 196px;width:196px;min-width:196px;gap:13px;overflow-y:auto;scrollbar-width:none;min-height:0;'
              : COL + 'position:absolute;left:0;top:0;bottom:0;width:252px;z-index:6;gap:13px;overflow-y:auto;scrollbar-width:thin;min-height:0;background:#FBF8F1;border-right:1px solid rgba(34,39,46,.16);box-shadow:14px 0 34px rgba(34,39,46,.13);padding:0 14px 12px 0;',
          )}
        >
          <div style={sx('min-width:0;')}>
            <div style={sx(HEAD + 'padding-bottom:4px;')}>Asking about</div>
            {c && !c.scope.fixed && (
              <div style={sx('display:flex;flex-wrap:wrap;gap:4px;margin-top:6px;min-width:0;')}>
                {scopes.map((x) => (
                  <a
                    key={x.name}
                    href="#"
                    aria-pressed={x.id === scopeId}
                    onClick={(e) => {
                      stop(e);
                      setScopePick(x.id);
                    }}
                    style={sx(
                      'display:inline-flex;align-items:center;height:23px;padding:0 9px;border-radius:12px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;transition:background .18s ' +
                        EASE +
                        ';' +
                        (x.id === scopeId ? 'background:' + BLUE + ';color:#FCFAF4;' : 'background:rgba(34,39,46,.06);color:' + MUTE + ';'),
                    )}
                  >
                    {x.name}
                  </a>
                ))}
              </div>
            )}
            {c?.scope.fixed && (
              <>
                <div style={sx('font-size:16px;font-weight:700;color:#22272E;margin-top:4px;')}>{scopeName}</div>
                <div style={sx('font-size:14px;color:#54606C;line-height:1.45;margin-top:2px;text-wrap:pretty;')}>{c.scope.note}</div>
              </>
            )}
          </div>

          <div style={sx('min-width:0;')}>
            <div style={sx('display:flex;align-items:baseline;gap:7px;min-width:0;')}>
              <span style={sx('flex:1 1 auto;min-width:0;font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;')}>Connected systems</span>
              <span style={sx('flex:0 0 auto;font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>{c ? `${reachN} of ${c.systems.length}` : ''}</span>
            </div>
            {(c?.systems ?? []).map((y) => (
              <a
                key={y.key}
                href="#"
                onClick={y.inReach ? go(y.view) : stop}
                aria-disabled={!y.inReach || undefined}
                title={y.inReach ? 'Open ' + y.name : 'Not in your reach at your role: ' + noDash(y.whoCan ?? 'an operator can')}
                style={sx('display:flex;align-items:baseline;gap:7px;padding:4px 0;border-top:1px solid rgba(34,39,46,.14);text-decoration:none;min-width:0;transition:color .15s ' + EASE + ';color:' + (y.inReach ? INK : '#54606C') + ';')}
              >
                <span style={sx('flex:0 0 6px;width:6px;height:6px;border-radius:50%;align-self:center;background:' + (y.inReach ? OK : '#C6C0B4') + ';')} />
                <span style={sx('flex:1 1 60px;min-width:0;font-size:14px;font-weight:600;overflow-wrap:break-word;')}>{y.name}</span>
                <span style={sx('flex:0 0 auto;font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>{y.inReach ? y.meta : 'out of reach'}</span>
              </a>
            ))}
          </div>

          <div style={sx('min-width:0;')}>
            <div style={sx(HEAD + 'padding-bottom:4px;')}>Earlier</div>
            {(c?.conversations ?? []).map((cv) => (
              <a
                key={cv.id}
                href="#"
                onClick={(e) => {
                  stop(e);
                  ask(cv.question);
                }}
                style={sx('display:block;min-width:0;padding:5px 0;border-top:1px solid rgba(34,39,46,.14);text-decoration:none;color:#3C454F;transition:color .15s ' + EASE + ';')}
              >
                <span style={sx('display:block;min-width:0;font-size:14px;font-weight:600;overflow-wrap:break-word;')}>{cv.name}</span>
                <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:1px;overflow-wrap:break-word;')}>{convWhen(cv.at, cv.note, now)}</span>
              </a>
            ))}
          </div>

          <div style={sx('min-width:0;')}>
            <div style={sx(HEAD + 'padding-bottom:4px;')}>What it remembers</div>
            {(c?.memory ?? []).map((m) => {
              const gone = forgot[m.id] ?? !!m.forgotten;
              return (
                <div key={m.id} style={sx('min-width:0;padding:5px 0;border-top:1px solid rgba(34,39,46,.14);')}>
                  <div style={sx('font-size:14px;color:#22272E;line-height:1.4;text-wrap:pretty;')}>
                    <b style={sx('font-weight:700;')}>{m.key}</b> <span>{gone ? 'forgotten. It will not steer another answer.' : m.value}</span>
                  </div>
                  <div style={sx('display:flex;align-items:baseline;gap:8px;margin-top:2px;min-width:0;')}>
                    <span style={sx('flex:1 1 auto;min-width:0;font-size:14px;color:' + (gone ? RED : m.expiresAt ? AMBER : MUTE) + ';')}>
                      {gone ? 'removed just now' : m.expiresAt ? `expires in ${Math.max(0, Math.ceil((Date.parse(m.expiresAt) - now) / 86_400_000))}d` : 'no expiry'}
                    </span>
                    <a
                      href="#"
                      aria-disabled={!!roReason || undefined}
                      title={roReason ?? undefined}
                      onClick={(e) => {
                        stop(e);
                        if (!roReason) void forget(m.id, !gone);
                      }}
                      style={sx('flex:0 0 auto;font-size:14px;font-weight:700;color:#B3261E;text-decoration:none;')}
                    >
                      {gone ? 'Put it back' : 'Forget'}
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={sx(COL + 'flex:1 1 auto;min-width:0;' + (railOpen && W < 1120 && !stacked ? 'visibility:hidden;' : ''))}>
        <div ref={scrollRef} data-atk-scroll="1" style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;display:flex;flex-direction:column;min-width:0;padding-right:6px;')}>
          {brief.data && (
            <div data-testid="atlas-opening" style={sx('min-width:0;padding:16px 0;border-top:none;')}>
              <div style={sx('font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:' + BLUE + ';')}>ATLAS</div>
              {/* Every role's Atlas greets the person by name, by their building's clock (Andrew, 2026-10-19). */}
              <div data-testid="atlas-greeting" style={sx('font-size:16px;font-weight:800;letter-spacing:-.01em;color:' + INK + ';margin-top:4px;')}>{`${hello}.`}</div>
              <div translate="no" style={sx(TEXT)}>{brief.data.text}</div>
              {brief.data.evidence && <EvidenceBlock ev={brief.data.evidence} now={now} />}
              {suggestions.length > 0 && <Follow list={suggestions} onAsk={ask} />}
            </div>
          )}
          {thread.map((t, i) => (
            <TurnView
              key={i}
              t={t}
              first={i === 0 && !brief.data}
              arriving={i === thread.length - 1 && t.kind === 'atlas' && !t.answer.done && !t.error}
              now={now}
              expanded={openTrace === i}
              onTrace={() => setOpenTrace((o) => (o === i ? null : i))}
              onAsk={ask}
              onDry={openDry}
              ranIds={ranIds}
              onDo={(d) => void doIt(i, d)}
              onCancel={() => cancelDry(i)}
              roReason={roReason}
            />
          ))}
        </div>

        {scribeShow && (
          <div data-testid="atlas-scribe-strip" style={sx('flex:0 0 auto;min-width:0;padding:10px 0 12px;')}>
            <div style={sx('display:flex;align-items:baseline;gap:10px;min-width:0;padding-bottom:4px;')}>
              <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>From the scribe today</span>
              <span style={sx('font-size:14px;color:#54606C;')}>
                {sess.filter((x) => x.state !== 'SIGNED').length + ' waiting for a signature · ' + sess.filter((x) => x.state === 'SIGNED').length + ' signed'}
              </span>
              <a href="#" onClick={(e) => { stop(e); onScribe(); }} style={sx('margin-left:auto;font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}>
                Open Scribe {'→'}
              </a>
            </div>
            <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:0 22px;min-width:0;')}>
              {sess.map((x) => (
                <a
                  key={x.id}
                  href="#"
                  onClick={(e) => { stop(e); onScribe(); }}
                  style={sx('display:flex;flex-direction:column;gap:3px;min-width:0;padding:8px 0;border-top:1px solid rgba(34,39,46,.1);text-decoration:none;')}
                >
                  <span style={sx('display:flex;align-items:center;gap:8px;min-width:0;')}>
                    <span style={sx('font-size:16px;font-weight:700;color:#22272E;min-width:0;overflow-wrap:break-word;')}>
                      <span>{x.who}</span> · <span>{x.type}</span>
                    </span>
                    <span style={sx('margin-left:auto;' + x.stStyle)}>{x.state}</span>
                  </span>
                  <span style={sx('font-size:14px;color:#54606C;')}>
                    <span>{x.when}</span> · <span>{x.note}</span>
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}

        <div style={sx('flex:0 0 auto;min-width:0;padding-top:11px;border-top:1px solid rgba(34,39,46,.14);')}>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:7px;min-width:0;')}>
            <input
              ref={inputRef}
              type="text"
              aria-label="Ask Atlas"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  ask(input.trim() || suggestions[0] || '');
                }
              }}
              placeholder={placeholder}
              style={sx('box-sizing:border-box;flex:1 1 200px;min-width:min(100%,170px);height:42px;padding:0 14px;border:1px solid rgba(34,39,46,.2);border-radius:9px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;')}
            />
            <a
              href="#"
              onClick={(e) => {
                stop(e);
                inputRef.current?.focus();
              }}
              title="Ask out loud: your device's own dictation types into the box"
              aria-label="Dictate"
              style={sx('flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;width:42px;height:42px;border-radius:9px;text-decoration:none;transition:background .18s ' + EASE + ';background:rgba(34,39,46,.06);color:' + MUTE + ';')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                <rect x="9" y="3" width="6" height="11" rx="3" />
                <path d="M5 11a7 7 0 0 0 14 0" />
                <path d="M12 18v3" />
              </svg>
            </a>
            <a
              href="#"
              onClick={(e) => {
                stop(e);
                ask(input.trim() || suggestions[0] || '');
              }}
              style={sx('flex:0 0 auto;display:inline-flex;align-items:center;height:42px;padding:0 18px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;')}
            >
              Ask
            </a>
          </div>
          <div style={sx('font-size:14px;color:#54606C;line-height:1.5;margin-top:7px;text-wrap:pretty;')}>
            Atlas reads only what your role may read. Nothing it proposes runs without a signature, and every answer is sealed.
          </div>
        </div>
      </div>
    </div>
  );
}

/** '19 Aug · you held agency cover', or 'yesterday 16:20' (the design's Earlier list). */
function convWhen(at: string, note: string | null, now: number): string {
  const d = new Date(at);
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const day = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  if (note) return `${day} · ${note}`;
  const y = new Date(now - 86_400_000);
  if (d.toDateString() === new Date(now).toDateString()) return `today ${hm}`;
  if (d.toDateString() === y.toDateString()) return `yesterday ${hm}`;
  return `${day} ${hm}`;
}

function Follow({ list, onAsk }: { list: string[]; onAsk: (q: string) => void }) {
  return (
    <div style={sx('display:flex;flex-wrap:wrap;gap:6px;margin-top:11px;min-width:0;')}>
      {list.map((f) => (
        <a
          key={f}
          href="#"
          onClick={(e) => {
            stop(e);
            onAsk(f);
          }}
          style={sx(FOLLOW)}
        >
          {f}
        </a>
      ))}
    </div>
  );
}

function TurnView({
  t,
  first,
  arriving,
  now,
  expanded,
  onTrace,
  onAsk,
  onDry,
  ranIds,
  onDo,
  onCancel,
  roReason,
}: {
  t: Turn;
  first: boolean;
  arriving: boolean;
  now: number;
  expanded: boolean;
  onTrace: () => void;
  onAsk: (q: string) => void;
  onDry: (d: AtlasDryRun) => void;
  ranIds: Set<string>;
  onDo: (d: AtlasDryRun) => void;
  onCancel: () => void;
  roReason: string | null;
}) {
  const go = useGo();
  const navigate = useNavigate();
  const { role } = useConsoleRole();
  const showVendor = !!role && VENDOR_ROLES.has(role);
  const isYou = t.kind === 'you';
  const wrap = 'min-width:0;padding:16px 0;border-top:' + (first ? 'none' : '1px solid rgba(34,39,46,.14)') + ';' + (arriving ? 'animation:riseIn .34s ' + EASE + ';' : '');
  const who = (
    <div style={sx('font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:' + (isYou ? '#54606C' : BLUE) + ';')}>{isYou ? 'YOU' : 'ATLAS'}</div>
  );

  if (t.kind === 'you') {
    return (
      <div data-testid="atlas-turn-you" style={sx(wrap)}>
        {who}
        <div translate="no" style={sx(TEXT_YOU)}>{t.text}</div>
      </div>
    );
  }

  if (t.kind === 'ran') {
    return (
      <div style={sx(wrap)}>
        {who}
        <div translate="no" style={sx(TEXT)}>{t.text}</div>
        <Marks marks={[{ label: 'RAN', tone: 'ok' }, ...(t.sealed ? [{ label: 'SEALED ' + t.sealed, tone: 'mute' as AtlasTone }] : [])]} />
      </div>
    );
  }

  if (t.kind === 'dry') {
    const done = !!t.ran;
    return (
      <div data-testid="atlas-dry" style={sx(wrap)}>
        {who}
        <div style={sx('min-width:0;margin-top:9px;border-left:2px solid ' + (done ? OK : BLUE) + ';padding:10px 0 10px 13px;;' + (done ? 'opacity:.72;' : ''))}>
          <div style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:4px;min-width:0;')}>
            <span style={sx('flex:1 1 150px;min-width:0;font-size:16px;font-weight:700;letter-spacing:-.012em;color:#22272E;text-wrap:pretty;')}>{t.dry.title}</span>
            <span style={sx(mark(done ? 'ok' : 'amber'))}>{done ? 'RAN · SEALED' : 'NOTHING HAS RUN YET'}</span>
          </div>
          {t.dry.effects.map((f) => (
            <div key={f.k} style={sx('display:flex;align-items:baseline;gap:11px;padding:5px 0;border-top:1px solid rgba(34,39,46,.1);min-width:0;')}>
              <span style={sx('flex:0 0 78px;font-size:14px;font-weight:700;letter-spacing:.07em;color:#54606C;')}>{f.k}</span>
              <span style={sx('flex:1 1 130px;min-width:0;font-size:14px;color:#22272E;line-height:1.5;text-wrap:pretty;')}>{noDash(f.v)}</span>
            </div>
          ))}
          <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;margin-top:10px;min-width:0;')}>
            {!done && (
              <>
                <a
                  href="#"
                  aria-disabled={!!roReason || t.busy || undefined}
                  title={roReason ?? undefined}
                  onClick={(e) => {
                    stop(e);
                    if (!roReason && !t.busy) onDo(t.dry);
                  }}
                  style={sx('display:inline-flex;align-items:center;height:30px;padding:0 14px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;')}
                >
                  Sign and run it
                </a>
                <a href="#" onClick={(e) => { stop(e); onCancel(); }} style={sx('font-size:14px;font-weight:600;color:#54606C;text-decoration:none;white-space:nowrap;')}>
                  Cancel
                </a>
              </>
            )}
            <span style={sx('flex:1 1 170px;min-width:0;font-size:14px;color:#54606C;line-height:1.5;text-wrap:pretty;')}>{done ? t.ran : noDash(t.dry.gate)}</span>
          </div>
        </div>
      </div>
    );
  }

  const a = t.answer;
  const refused = refusedOf(a);
  const settled = a.done || !!t.error;
  const waiting = !settled;
  const steps = stepsOf(a, showVendor);
  const showTrace = (waiting && steps.length > 0) || (settled && expanded);
  const cold = waiting && !a.text && a.status.length > 0;
  const skeleton = waiting && !a.text && a.status.length === 0;
  const text = t.error ?? noDash(a.text);
  const marks = marksOf(a, t.error);
  const acts = refused || t.error ? [] : a.proposals;
  // Each incident, resident or alert the answer read, linked to its own page (only pages this role opens).
  const links = refused || t.error ? [] : consoleAnswerLinks(a, role);
  return (
    <div data-testid="atlas-turn-atlas" style={sx(wrap)}>
      {who}
      {cold ? (
        <>
          <div style={sx(TEXT)}>Atlas is getting ready. The first answer after an update takes about forty seconds. You will see each step as it happens, and if one fails you will see which.</div>
          <div style={sx('margin-top:9px;min-width:0;')}>
            {a.status.map((cs, k) => {
              const color = cs.state === 'done' ? OK : cs.state === 'now' ? INK : DIM;
              return (
                <div key={k} style={sx('display:flex;align-items:baseline;gap:9px;padding:3px 0;min-width:0;')}>
                  <span style={sx('flex:0 0 12px;font-size:14px;color:' + color + ';')}>{cs.state === 'done' ? '✓' : cs.state === 'now' ? '▸' : '·'}</span>
                  <span style={sx('flex:1 1 110px;min-width:0;font-size:14px;color:' + color + ';line-height:1.45;')}>{cs.label}</span>
                  <span style={sx('flex:0 0 auto;font-size:14px;font-variant-numeric:tabular-nums;color:#54606C;')}>{cs.t ?? (cs.state === 'now' ? 'running' : '')}</span>
                </div>
              );
            })}
            <div style={sx('height:3px;border-radius:2px;background:rgba(34,39,46,.09);margin-top:7px;overflow:hidden;')}>
              <i style={sx('display:block;height:100%;border-radius:2px;background:' + BLUE + ';width:' + Math.round((100 * a.status.filter((s) => s.state === 'done').length) / Math.max(1, a.status.length)) + '%;transition:width .5s ' + EASE + ';')} />
            </div>
          </div>
        </>
      ) : (
        text && <div translate="no" style={sx(TEXT)}>{text}</div>
      )}

      {showTrace && (
        <div data-testid="atlas-trace" style={sx('margin-top:9px;min-width:0;border-left:2px solid rgba(47,92,151,.3);padding-left:11px;')}>
          {steps.map((s, k) => (
            <div key={k} style={sx(TRACE_ROW)}>
              <span style={sx('flex:0 0 56px;font-size:14px;font-weight:700;letter-spacing:.08em;color:' + (k === 3 ? (refused || t.error ? RED : OK) : BLUE) + ';')}>{s.step}</span>
              <span style={sx('flex:1 1 110px;min-width:0;font-size:14px;color:#54606C;line-height:1.5;text-wrap:pretty;')}>{s.detail}</span>
              <span style={sx('flex:0 0 auto;font-size:14px;font-variant-numeric:tabular-nums;color:#54606C;')}>{msText(s.ms)}</span>
            </div>
          ))}
        </div>
      )}

      {skeleton && (
        <div style={sx('margin-top:10px;min-width:0;')}>
          <div style={sx(HEAD + 'margin-bottom:7px;')}>Pulling the records</div>
          {[94, 80, 87, 71].map((w, k) => (
            <div key={k} style={sx('height:10px;width:' + w + '%;border-radius:3px;background:rgba(34,39,46,.07);margin-bottom:8px;animation:breathe 1.3s ' + EASE + ' infinite;animation-delay:' + k * 130 + 'ms;')} />
          ))}
        </div>
      )}

      {settled && a.evidence && !refused && !t.error && <EvidenceBlock ev={a.evidence} now={now} />}

      {settled && a.assessment && !refused && !t.error && (
        <div style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:3px;margin-top:10px;min-width:0;')}>
          <span style={sx('flex:0 0 auto;font-size:17px;font-weight:700;font-variant-numeric:tabular-nums;letter-spacing:-.02em;color:' + (a.assessment.confidence >= 90 ? OK : AMBER) + ';')}>
            {Math.round(a.assessment.confidence) + '%'}
          </span>
          <span style={sx('flex:1 1 150px;min-width:0;font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>{noDash(a.assessment.note)}</span>
        </div>
      )}
      {settled && a.assessment?.against && !refused && !t.error && (
        <div style={sx('font-size:14px;color:#4A5866;line-height:1.55;margin-top:7px;max-width:66ch;text-wrap:pretty;')}>
          <b style={sx('font-weight:700;')}>Against it:</b> <span>{noDash(a.assessment.against)}</span>
        </div>
      )}

      {settled && (
        <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;row-gap:5px;margin-top:9px;min-width:0;')}>
          {marks.map((k) => (
            <span key={k.label} style={sx(mark(k.tone))}>{k.label}</span>
          ))}
          {!refused &&
            a.sources.map((s) => (
              <a key={s.label} href="#" onClick={go(s.view)} style={sx('flex:0 0 auto;font-size:14px;font-weight:600;color:#1D3F6B;text-decoration:none;')}>
                {s.label}
              </a>
            ))}
          {steps.length > 0 && (
            <a
              href="#"
              aria-expanded={expanded}
              onClick={(e) => {
                stop(e);
                onTrace();
              }}
              style={sx('flex:0 0 auto;font-size:14px;font-weight:600;color:#54606C;text-decoration:none;white-space:nowrap;')}
            >
              {expanded ? 'hide working' : 'show working'}
            </a>
          )}
        </div>
      )}

      {settled && links.length > 0 && (
        <div data-testid="atlas-answer-links" style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;row-gap:6px;margin-top:9px;min-width:0;')}>
          {links.map((l) => (
            <a
              key={l.path}
              href={l.path}
              onClick={(e) => {
                stop(e);
                navigate(l.path);
              }}
              style={sx('flex:0 1 auto;min-width:0;max-width:100%;padding:4px 10px;border-radius:12px;background:rgba(79,138,214,.08);border:1px solid rgba(47,92,151,.22);font-size:14px;font-weight:600;color:#1D3F6B;text-decoration:none;overflow-wrap:break-word;')}
            >
              {noDash(l.label)}
            </a>
          ))}
        </div>
      )}

      {settled && acts.length > 0 && (
        <div style={sx('display:flex;align-items:center;gap:7px;flex-wrap:wrap;row-gap:6px;margin-top:10px;min-width:0;')}>
          {acts.map((d) => {
            const ran = ranIds.has(d.id);
            return (
              <a
                key={d.id}
                href="#"
                aria-disabled={ran || undefined}
                onClick={(e) => {
                  stop(e);
                  if (!ran) onDry(d);
                }}
                style={sx(
                  'display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;transition:background .18s ' +
                    EASE +
                    ';' +
                    (ran ? 'color:' + OK + ';background:rgba(31,107,82,.12);' : 'color:#FCFAF4;background:' + BLUE + ';'),
                )}
              >
                {ran ? 'Done · ' + d.label : d.label}
              </a>
            );
          })}
        </div>
      )}

      {t.error && (
        <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;margin-top:10px;min-width:0;')}>
          <a
            href="#"
            onClick={(e) => {
              stop(e);
              onAsk(t.q);
            }}
            style={sx('display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;')}
          >
            Try it again
          </a>
          <span style={sx('flex:1 1 180px;min-width:0;font-size:14px;color:#54606C;line-height:1.5;text-wrap:pretty;')}>Nothing was retrieved and nothing was composed, so there is nothing to undo.</span>
        </div>
      )}

      {settled && a.followUps.length > 0 && <Follow list={a.followUps} onAsk={onAsk} />}
    </div>
  );
}

function Marks({ marks }: { marks: { label: string; tone: AtlasTone }[] }) {
  return (
    <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;row-gap:5px;margin-top:9px;min-width:0;')}>
      {marks.map((k) => (
        <span key={k.label} style={sx(mark(k.tone))}>{k.label}</span>
      ))}
    </div>
  );
}

